import { useEffect, useState } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { useToast } from './Toast';
import { formatoStock } from './StockSku';
import { Confirm } from './Confirm';
import { variacionesSinVariante, type PublicacionesParaAviso, type VariantesDeSku } from '../lib/variantes';

/** Bloque «Variantes» de la ficha de un SKU: color, publicaciones y stock de cada variante, Sin asignar y total. No se muestra si el SKU no tiene variantes. */
export function VariantesSku({ sku, puedeEditar, onCargadas, publicaciones }: {
  sku: string; puedeEditar: boolean; onCargadas?: (v: VariantesDeSku) => void;
  /** Publicaciones del SKU (directas y las que llegan por packs/combos): sirven para avisar, al prender el switch, qué variaciones sin variante publicarían 0. */
  publicaciones?: PublicacionesParaAviso;
}) {
  const toast = useToast();
  const [datos, setDatos] = useState<VariantesDeSku | null>(null);
  const [confirmarSwitch, setConfirmarSwitch] = useState(false);

  const aplicar = (v: VariantesDeSku) => { setDatos(v); onCargadas?.(v); };

  useEffect(() => {
    let vigente = true;
    setDatos(null);
    api.get<VariantesDeSku>(`/api/v3/catalogo/${encodeURIComponent(sku)}/variantes`)
      .then((v) => { if (vigente && v?.variantes?.length) aplicar(v); })
      .catch(() => { /* sin variantes visibles: el bloque no se muestra */ });
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  if (!datos || datos.variantes.length === 0) return null;

  const guardarColor = async (id: number, colorHex: string) => {
    try { aplicar(await api.patch<VariantesDeSku>(`/api/v3/catalogo/${encodeURIComponent(sku)}/variantes/${id}`, { colorHex })); }
    catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
  };
  const guardarSwitch = async (stockPorVarianteEnMl: boolean) => {
    try {
      await api.patch(`/api/v3/catalogo/${encodeURIComponent(sku)}`, { stockPorVarianteEnMl });
      aplicar({ ...datos, stockPorVarianteEnMl });
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
  };

  const { seguras: sueltas, posibles } = variacionesSinVariante(publicaciones, datos.variantes);
  const consecuencias = [
    datos.sinAsignar > 0 ? `• «Sin asignar» (${formatoStock(datos.sinAsignar)}) no se publica en ninguna variación.` : null,
    sueltas.length > 0
      ? `• ${sueltas.length} ${sueltas.length === 1 ? 'variación sin variante ligada publicará' : 'variaciones sin variante ligada publicarán'} 0: ${sueltas.join(', ')}.`
      : null,
    posibles.length > 0
      ? `• ${posibles.length} ${posibles.length === 1 ? 'variación de un pack o combo podría' : 'variaciones de packs o combos podrían'} publicar 0 si no tienen variante ligada: ${posibles.join(', ')}.`
      : null,
  ].filter(Boolean);
  const mensajeSwitch = [
    'Mercado Libre va a recibir el stock de cada variante en lugar del total del SKU.',
    ...consecuencias,
    consecuencias.length > 0 ? 'Lo recomendado es repartir todo el stock (Sin asignar en 0) y ligar cada variación antes de prender.' : null,
  ].filter(Boolean).join('\n');

  return (
    <section aria-label="Variantes del SKU" className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-700">Variantes</h3>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500">
            <th className="font-medium pr-3">Color</th><th className="font-medium pr-3">Variante</th>
            <th className="font-medium pr-3">Publicaciones</th><th className="font-medium text-right">Stock</th>
          </tr>
        </thead>
        <tbody>
          {datos.variantes.map((v) => (
            <tr key={v.id} className="border-t">
              <td className="pr-3 py-1">
                <input type="color" aria-label={`Color de ${v.nombre}`} defaultValue={v.colorHex ?? '#cccccc'} disabled={!puedeEditar}
                  onBlur={(e) => { if (e.target.value !== (v.colorHex ?? '#cccccc')) void guardarColor(v.id, e.target.value); }} />
              </td>
              <td className="pr-3">{v.nombre}</td>
              <td className="pr-3 text-xs text-slate-600">
                {v.publicaciones.map((p) => (p.variantId ? `${p.mlaId}/${p.variantId}` : p.mlaId)).join(', ') || '—'}
              </td>
              <td className="text-right">{formatoStock(v.cantidad)}</td>
            </tr>
          ))}
          <tr className="border-t">
            <td /><td className="pr-3">Sin asignar</td>
            <td className="pr-3 text-xs text-amber-700">{datos.aviso}</td>
            <td className={`text-right ${datos.sinAsignar < 0 ? 'text-red-700' : ''}`}>{formatoStock(datos.sinAsignar)}</td>
          </tr>
          {(datos.cuarentena ?? 0) > 0 && (
            <tr className="border-t">
              <td /><td className="pr-3">En cuarentena</td>
              <td className="pr-3 text-xs text-slate-600">No es de ningún color hasta liberarla.</td>
              <td className="text-right">{formatoStock(datos.cuarentena ?? 0)}</td>
            </tr>
          )}
          <tr className="border-t font-semibold">
            <td /><td className="pr-3">Total</td><td /><td className="text-right">{formatoStock(datos.fisico)}</td>
          </tr>
        </tbody>
      </table>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" role="switch" checked={datos.stockPorVarianteEnMl} disabled={!puedeEditar}
          onChange={(e) => (e.target.checked ? setConfirmarSwitch(true) : void guardarSwitch(false))} />
        Publicar en Mercado Libre el stock de cada variante
      </label>
      <p className="text-xs text-slate-500">«Sin asignar» es el stock del SKU que todavía no se repartió entre variantes y no se publica por variación. Prendé el interruptor cuando quede en 0.</p>
      <Confirm open={confirmarSwitch} title="Publicar el stock de cada variante en Mercado Libre" message={mensajeSwitch} confirmLabel="Prender"
        onCancel={() => setConfirmarSwitch(false)} onConfirm={() => { setConfirmarSwitch(false); void guardarSwitch(true); }} />
    </section>
  );
}
